import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { uploadDealPhoto, type DealPhoto } from '@/lib/db/admin/deal-photos';
import {
  IMAGE_MIN_COVER_HEIGHT,
  IMAGE_MIN_COVER_WIDTH,
  IMAGE_MIN_GALLERY_HEIGHT,
  IMAGE_MIN_GALLERY_WIDTH,
  IMAGE_MAX_RATIO,
  IMAGE_MIN_RATIO,
  validateImageDimensions,
  type ImageRole,
} from '@/lib/storage/image-rules';
import { readImageDimensions } from '@/lib/storage/read-image-dimensions';

import './DealPhotoUploader.css';

const ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

/**
 * Raw file ceiling enforced before client-side compression runs. 25 MB
 * leaves ample room for smartphone HEIC/JPEG exports (typically 6–12 MB)
 * and lets `compressIfNeeded` (5 MB threshold → 2 MB target) take the
 * decision. Lower values blocked the very compression path Decision D4
 * was meant to exercise.
 */
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

type QueueStatus = 'queued' | 'compressing' | 'uploading' | 'done' | 'error';

type QueueEntry = {
  id: string;
  file: File;
  status: QueueStatus;
  errorMessage?: string;
};

export type DealPhotoUploaderProps = {
  dealId: string;
  /** Disabled when the gallery is at the 15-photo cap. */
  disabled?: boolean;
  /** Number of photos already attached — used to compute remaining slots. */
  currentPhotoCount: number;
  onPhotoUploaded: (photo: DealPhoto) => void;
  /** Bubbles up the count of in-flight uploads so the parent can mark the
   *  form dirty / block navigation while uploads are running. */
  onActiveUploadsChange?: (count: number) => void;
};

const MAX_PHOTOS_PER_DEAL = 15;

export function DealPhotoUploader({
  dealId,
  disabled = false,
  currentPhotoCount,
  onPhotoUploaded,
  onActiveUploadsChange,
}: DealPhotoUploaderProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  // Stable counter for queue item IDs without depending on Date.now() (prone
  // to ties when multiple files are dropped in the same millisecond).
  const counterRef = useRef(0);

  const remainingSlots = Math.max(0, MAX_PHOTOS_PER_DEAL - currentPhotoCount);
  const effectivelyDisabled = disabled || remainingSlots === 0;

  const activeCount = queue.filter(
    (entry) =>
      entry.status === 'queued' || entry.status === 'compressing' || entry.status === 'uploading',
  ).length;

  useEffect(() => {
    onActiveUploadsChange?.(activeCount);
  }, [activeCount, onActiveUploadsChange]);

  const updateEntry = useCallback((id: string, patch: Partial<QueueEntry>) => {
    setQueue((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  }, []);

  const processFile = useCallback(
    async (entry: QueueEntry) => {
      try {
        updateEntry(entry.id, { status: 'compressing' });
        // We let uploadDealPhoto decide whether to actually compress
        // (compressIfNeeded is a no-op below the threshold).
        updateEntry(entry.id, { status: 'uploading' });
        const photo = await uploadDealPhoto(dealId, entry.file);
        updateEntry(entry.id, { status: 'done' });
        onPhotoUploaded(photo);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        updateEntry(entry.id, { status: 'error', errorMessage: message });
        toast.error(t('admin.dealEdit.media.uploadFailedToast'));
      }
    },
    [dealId, onPhotoUploaded, t, updateEntry],
  );

  const onDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        const reason = rejections[0]?.errors[0];
        if (reason?.code === 'file-too-large') {
          toast.error(t('admin.dealEdit.media.fileTooLargeToast'));
        } else if (reason?.code === 'file-invalid-type') {
          toast.error(t('admin.dealEdit.media.unsupportedFormatToast'));
        } else {
          toast.error(t('admin.dealEdit.media.rejectedToast'));
        }
      }

      if (accepted.length === 0) return;

      // Cap at the remaining slots so we don't queue uploads that the
      // 15-photo trigger will reject anyway.
      const cappedAccepted = accepted.slice(0, remainingSlots);
      if (cappedAccepted.length < accepted.length) {
        toast.error(t('admin.dealEdit.media.tooManyPhotosToast'));
      }

      // Pre-validate intrinsic dimensions + ratio (Lot B.2). Role for each
      // file is determined by the position it would land at — first slot of
      // a deal with zero photos is the cover, the rest are gallery. Toast
      // the first failure verbosely so the admin learns the rule rather
      // than seeing a generic "rejected" message.
      const validated: File[] = [];
      for (const [indexInBatch, file] of cappedAccepted.entries()) {
        const role: ImageRole = currentPhotoCount + indexInBatch === 0 ? 'cover' : 'gallery';
        let dimensions: { width: number; height: number };
        try {
          dimensions = await readImageDimensions(file);
        } catch {
          toast.error(t('admin.dealEdit.media.dimensionsReadFailedToast'));
          continue;
        }
        const result = validateImageDimensions(dimensions.width, dimensions.height, role);
        if (!result.ok) {
          if (result.error.code === 'tooSmallCover') {
            toast.error(
              t('admin.dealEdit.media.tooSmallCoverToast', {
                width: result.error.width,
                height: result.error.height,
                minWidth: IMAGE_MIN_COVER_WIDTH,
                minHeight: IMAGE_MIN_COVER_HEIGHT,
              }),
            );
          } else if (result.error.code === 'tooSmallGallery') {
            toast.error(
              t('admin.dealEdit.media.tooSmallGalleryToast', {
                width: result.error.width,
                height: result.error.height,
                minWidth: IMAGE_MIN_GALLERY_WIDTH,
                minHeight: IMAGE_MIN_GALLERY_HEIGHT,
              }),
            );
          } else {
            toast.error(
              t('admin.dealEdit.media.ratioOutOfBoundsToast', {
                ratio: result.error.ratio.toFixed(2),
                minRatio: IMAGE_MIN_RATIO.toFixed(2),
                maxRatio: IMAGE_MAX_RATIO.toFixed(2),
              }),
            );
          }
          continue;
        }
        validated.push(file);
      }

      if (validated.length === 0) return;

      const entries: QueueEntry[] = validated.map((file) => ({
        id: `${Date.now()}-${counterRef.current++}`,
        file,
        status: 'queued',
      }));

      setQueue((prev) => [...prev, ...entries]);
      // Process serially: keeps the upload bandwidth predictable on Free
      // and means the next entry sees a deterministic `currentPhotoCount`
      // for position assignment server-side.
      void (async () => {
        for (const entry of entries) {
          await processFile(entry);
        }
      })();
    },
    [currentPhotoCount, processFile, remainingSlots, t],
  );

  const dropzone = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_INPUT_BYTES,
    disabled: effectivelyDisabled,
    onDrop,
  });

  const dropzoneClass = useMemo(() => {
    const parts = ['admin-dropzone', 'deal-photo-uploader'];
    if (dropzone.isDragActive) parts.push('is-drag-active');
    if (effectivelyDisabled) parts.push('is-disabled');
    return parts.join(' ');
  }, [dropzone.isDragActive, effectivelyDisabled]);

  return (
    <div className="deal-photo-uploader__wrap">
      <div
        {...dropzone.getRootProps({ className: dropzoneClass })}
        aria-disabled={effectivelyDisabled}
      >
        <input {...dropzone.getInputProps({ id: inputId })} />
        <div className="admin-dropzone__title">{t('admin.dealEdit.media.dropzoneTitle')}</div>
        <div className="admin-dropzone__sub">
          {t('admin.dealEdit.media.dropzoneHint', {
            remaining: remainingSlots,
            max: MAX_PHOTOS_PER_DEAL,
          })}
        </div>
        <div className="admin-dropzone__standards">
          {t('admin.dealEdit.media.dropzoneStandardsHint')}
        </div>
      </div>

      {queue.length > 0 && (
        <ul className="deal-photo-uploader__queue" aria-live="polite">
          {queue.map((entry) => (
            <li
              key={entry.id}
              className={`deal-photo-uploader__item is-${entry.status}`}
              data-testid={`upload-queue-item-${entry.status}`}
            >
              <span className="deal-photo-uploader__item-name">{entry.file.name}</span>
              <span className="deal-photo-uploader__item-status">
                {t(`admin.dealEdit.media.queue.${entry.status}`)}
              </span>
              {entry.status === 'error' && entry.errorMessage && (
                <span className="deal-photo-uploader__item-error">{entry.errorMessage}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
